import { CSSProperties, ReactNode } from 'react';
import { Grid3x3, BarChart2 } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { Button, Card } from '../ui';

// Home 2 (symphonyHome2Web) — the Apps bento card (Figma 1859:2809), the left slot of Row 2.
//
// ⚠️ MOCK: orion-web has NO data source for "apps" (agent-created apps) yet, so this card renders a
// hardcoded placeholder list and carries a diagonal "MOCK" watermark so no one mistakes it for real
// data. It is only ever visible behind the symphonyHome2Web FT (dev/override-only today). When a real
// apps data source lands, replace MOCK_APPS + the stamp with the real hook and localize the item copy.

interface MockApp {
  name: string;
  subtitle: string;
  glyph: ReactNode;
  tile: string;
}

// Placeholder content only — not real product copy (see the MOCK note above).
const MOCK_APPS: MockApp[] = [
  {
    name: 'Weekend staffing calculator',
    subtitle: 'You created · 2d ago',
    glyph: <Grid3x3 size={16} aria-hidden />,
    tile: 'var(--ds-accent-10)',
  },
  {
    name: 'Supplier quote tracker',
    subtitle: 'Shared by Maestro',
    glyph: <BarChart2 size={16} aria-hidden />,
    tile: 'var(--ds-purple-200)',
  },
];
const MOCK_CREATED = 6;
const MOCK_SHARED = 1;

const CARD: CSSProperties = {
  borderColor: 'var(--ds-separator-default)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  position: 'relative',
  overflow: 'hidden',
};
const HEADER_LEFT: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };
const TITLE: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 16,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '20px',
  letterSpacing: '-0.32px',
  color: 'rgb(var(--ink-rgb))',
};
const LIST: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16, width: '100%' };
const ROW: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, width: '100%' };
const TILE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: 6,
  color: 'rgb(var(--ink-rgb))',
  flexShrink: 0,
};
const INFO: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 };
const NAME: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 13,
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'rgb(var(--ink-rgb))',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const SUBTITLE: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 11,
  color: 'var(--ds-text-secondary)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
// `marginTop: auto` sinks the divider + footer to the card's bottom, so the footer stays aligned when
// the card is stretched to match a taller sibling in the row.
const DIVIDER: CSSProperties = { height: 1, width: '100%', background: 'var(--ds-separator-light)', flexShrink: 0, marginTop: 'auto' };
const FOOTER: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%' };
const SUMMARY: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 13,
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'rgb(var(--ink-rgb))',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// The unmistakable "this is not real" watermark. Non-interactive, drawn over the whole card.
const STAMP: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  zIndex: 2,
};
const STAMP_TEXT: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 72,
  fontWeight: 800,
  letterSpacing: '0.15em',
  color: 'rgba(var(--ds-text-destructive-rgb), 0.16)',
  transform: 'rotate(-18deg)',
  userSelect: 'none',
};

export function Home2AppsCard() {
  const { t } = useTranslation();

  return (
    <Card style={CARD}>
      <div aria-hidden style={STAMP}>
        <span style={STAMP_TEXT}>MOCK</span>
      </div>

      <div style={HEADER_LEFT}>
        <Grid3x3 size={16} aria-hidden style={{ color: 'rgb(var(--ink-rgb))', flexShrink: 0 }} />
        <span style={TITLE}>{t('home2.apps.title')}</span>
      </div>

      <div style={LIST}>
        {MOCK_APPS.map((app) => (
          <div key={app.name} style={ROW}>
            <span style={{ ...TILE, background: app.tile }}>{app.glyph}</span>
            <span style={INFO}>
              <span style={NAME}>{app.name}</span>
              <span style={SUBTITLE}>{app.subtitle}</span>
            </span>
          </div>
        ))}
      </div>

      <div style={DIVIDER} />

      <div style={FOOTER}>
        <span style={SUMMARY}>{t('home2.apps.summary', { created: MOCK_CREATED, shared: MOCK_SHARED })}</span>
        {/* Inert while this card is mock — there is no apps destination yet. */}
        <Button size="medium" onClick={() => undefined}>
          {t('home2.apps.open')}
        </Button>
      </div>
    </Card>
  );
}
